import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ChatMarkdown from "./ChatMarkdown";

/**
 * Streaming reply text that arrives the way Gemini's does: line by line, each
 * new line fading in. Two parts:
 *
 * - The text is released at a steady pace instead of in network bursts, so
 *   lines fill at an even rhythm.
 * - A soft mask covers the bottom of the text. Its edge follows the text's
 *   height with a transition, so each line that wraps in fades up from
 *   transparent while the one above it settles to full ink.
 *
 * Styling lives in workshop.css (`.co-line-reveal`); reduced motion shows the
 * text as it lands, unmasked.
 */

/** Share of the unshown text released per frame. Bursts drain in ~0.3s. */
const CATCH_UP = 0.12;
/** Floor, in characters per frame, so a trickle still moves (~120 chars/s). */
const MIN_STEP = 2;
/** Within this of the bottom, the log counts as following the reply. */
const FOLLOW_SLACK_PX = 48;

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** The target text, released a few characters per frame. */
function useSteadyText(target: string): string {
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? target : ""));
  const shownRef = useRef(shown);

  useEffect(() => {
    // A new block, or text rewritten under us: start over from what matches.
    if (!target.startsWith(shownRef.current)) shownRef.current = "";
    if (shownRef.current.length === target.length) return;

    if (prefersReducedMotion()) {
      shownRef.current = target;
      const id = requestAnimationFrame(() => setShown(target));
      return () => cancelAnimationFrame(id);
    }

    let raf = 0;
    const tick = () => {
      const have = shownRef.current.length;
      const backlog = target.length - have;
      if (backlog <= 0) return;
      const step = Math.max(MIN_STEP, Math.ceil(backlog * CATCH_UP));
      const next = target.slice(0, have + step);
      shownRef.current = next;
      setShown(next);
      if (next.length < target.length) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return shown;
}

export default function StreamingLineReveal({
  content,
  live,
}: {
  content: string;
  /** Text is still arriving. Once false and caught up, the fade tail clears. */
  live: boolean;
}) {
  const shown = useSteadyText(content);
  const ref = useRef<HTMLDivElement>(null);

  // The mask's edge tracks the rendered height; the CSS transition on it is
  // what makes each new line fade in rather than appear.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const log = el.closest("[role='log']");
    let height = el.offsetHeight;
    const sync = () => {
      const next = el.offsetHeight;
      // Text grows between the transcript's renders, so its own scroll
      // follow can't see it. Keep a log that sat at the bottom there.
      if (log && next > height) {
        const before = log.scrollHeight - log.scrollTop - log.clientHeight - (next - height);
        if (before < FOLLOW_SLACK_PX) log.scrollTop = log.scrollHeight;
      }
      height = next;
      el.style.setProperty("--co-reveal-h", `${next}px`);
    };
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    // First line: start from nothing on the next frame so it fades in too.
    const id = requestAnimationFrame(sync);
    return () => {
      cancelAnimationFrame(id);
      observer.disconnect();
    };
  }, []);

  const settling = live || shown.length < content.length;

  return (
    <div ref={ref} className="co-line-reveal" data-live={settling || undefined}>
      <ChatMarkdown content={shown} isAnimating={settling} />
    </div>
  );
}
